import React, { useState, useEffect } from 'react';
import { BookTemplate, Gift } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { RegistryTemplate, TemplateItem } from '../types/templates';
import type { RegistryItem } from '../types/registry';

interface Props {
  onTemplateSelect: (items: Omit<RegistryItem, 'id' | 'registry_id' | 'is_claimed' | 'claimed_by' | 'created_at' | 'collected_amount'>[]) => void;
  currency: string;
}

const CURRENCY_RATES = {
  LKR: 1,      // Base currency
  USD: 0.0031, // 1 LKR = 0.0031 USD
  EUR: 0.0029,
  GBP: 0.0025,
  JPY: 0.46,
  AUD: 0.0047,
  CAD: 0.0042,
  SGD: 0.0042,
  INR: 0.26
};

export default function TemplateSelector({ onTemplateSelect, currency }: Props) {
  const [templates, setTemplates] = useState<RegistryTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [templateItems, setTemplateItems] = useState<TemplateItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (selectedTemplate) {
      loadTemplateItems();
    }
  }, [selectedTemplate, currency]);

  const convertPrice = (lkrPrice: number, targetCurrency: string): number => {
    const rate = CURRENCY_RATES[targetCurrency as keyof typeof CURRENCY_RATES] || 1;
    const convertedPrice = lkrPrice * rate;
    return Number(convertedPrice.toFixed(2));
  };

  const loadTemplates = async () => {
    try {
      const { data } = await supabase
        .from('registry_templates')
        .select('*')
        .order('category');
      
      if (data) setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateItems = async () => {
    try {
      const { data } = await supabase
        .from('template_items')
        .select('*')
        .eq('template_id', selectedTemplate)
        .order('priority');
      
      if (data) {
        const convertedItems = data.map(item => ({
          ...item,
          price: convertPrice(item.price, currency)
        }));
        setTemplateItems(convertedItems);
        
        const registryItems = convertedItems.map(item => ({
          name: item.name,
          price: item.price,
          priority: item.priority,
          url: '',
          allows_group_gifting: false,
          target_amount: null
        }));
        onTemplateSelect(registryItems);
      }
    } catch (error) {
      console.error('Error loading template items:', error);
    }
  };

  const categories = [...new Set(templates.map(t => t.category))];

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <BookTemplate className="w-5 h-5 text-white" />
        <h3 className="text-lg font-medium text-white">Start from a Template</h3>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-2">
          <div className="h-10 bg-gray-800/50 rounded" />
          <div className="h-40 bg-gray-800/50 rounded" />
        </div>
      ) : (
        <div className="space-y-4">
          <select
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
          >
            <option value="">Select a template...</option>
            {categories.map(category => (
              <optgroup key={category} label={category}>
                {templates
                  .filter(t => t.category === category)
                  .map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>

          {selectedTemplate && templateItems.length > 0 && (
            <div className="bg-gray-900/50 p-4 rounded-lg">
              <h4 className="font-medium text-white mb-2">Template Items</h4>
              <div className="space-y-2">
                {templateItems.map(item => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between bg-gray-800/50 p-3 rounded-md"
                  >
                    <div>
                      <p className="font-medium text-white">{item.name}</p>
                      <p className="text-sm text-gray-300">
                        {currency} {item.price}
                      </p>
                    </div>
                    <span className={`text-sm px-2 py-1 rounded ${
                      item.priority === 'high' ? 'bg-red-900/50 text-red-200' :
                      item.priority === 'medium' ? 'bg-yellow-900/50 text-yellow-200' :
                      'bg-green-900/50 text-green-200'
                    }`}>
                      {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}